import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContents } from '@/mocks/articleContent';

const heroImages: Record<string, string> = {
  'cssbuy-review': 'https://readdy.ai/api/search-image?query=Five%20gold%20rating%20stars%20floating%20above%20a%20clean%20desk%20with%20a%20tablet%20and%20notes%2C%20warm%20amber%20studio%20lighting%2C%20premium%20review%20concept%20photography%2C%20soft%20gradient%20background&width=1200&height=500&seq=art-hero-review&orientation=landscape',
  'cssbuy-coupon': 'https://readdy.ai/api/search-image?query=Gift%20voucher%20and%20discount%20tag%20with%20scissors%20on%20a%20coral%20pink%20background%2C%20playful%20ecommerce%20sale%20concept%2C%20bright%20studio%20lighting%2C%20clean%20minimal%20composition&width=1200&height=500&seq=art-hero-coupon&orientation=landscape',
  'cssbuy-warehouse': 'https://readdy.ai/api/search-image?query=Neat%20rows%20of%20cardboard%20parcels%20on%20industrial%20shelving%20in%20a%20bright%20modern%20warehouse%2C%20soft%20daylight%2C%20logistics%20storage%20concept%2C%20clean%20organized%20composition&width=1200&height=500&seq=art-hero-warehouse&orientation=landscape',
  'cssbuy-qc': 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20inspecting%20a%20folded%20garment%20on%20a%20sage%20green%20table%2C%20quality%20control%20inspection%20concept%2C%20soft%20natural%20window%20light%2C%20editorial%20product%20photography&width=1200&height=500&seq=art-hero-qc&orientation=landscape',
  'cssbuy-alternatives': 'https://readdy.ai/api/search-image?query=Two%20paths%20splitting%20on%20a%20minimal%20teal%20background%20with%20arrows%20pointing%20in%20different%20directions%2C%20choice%20and%20comparison%20concept%2C%20clean%20flat%20illustration%20style&width=1200&height=500&seq=art-hero-alternatives&orientation=landscape',
  'cssbuy-beginner-guide': 'https://readdy.ai/api/search-image?query=Open%20notebook%20with%20step%20by%20step%20checklist%20and%20coffee%20on%20a%20warm%20cream%20desk%2C%20learning%20and%20getting%20started%20concept%2C%20cozy%20natural%20light%2C%20editorial%20photography&width=1200&height=500&seq=art-hero-beginner&orientation=landscape',
  'cssbuy-shipping-brazil': 'https://readdy.ai/api/search-image?query=Cardboard%20parcel%20with%20green%20and%20yellow%20ribbon%20on%20a%20wooden%20table%2C%20Brazil%20shipping%20concept%2C%20warm%20tropical%20light%2C%20clean%20product%20photography&width=1200&height=500&seq=art-hero-brazil&orientation=landscape',
  'cssbuy-shipping-germany': 'https://readdy.ai/api/search-image?query=Cardboard%20parcel%20with%20black%20red%20and%20gold%20ribbon%20on%20a%20slate%20table%2C%20Germany%20shipping%20concept%2C%20cool%20studio%20lighting%2C%20clean%20product%20photography&width=1200&height=500&seq=art-hero-germany&orientation=landscape',
  'cssbuy-spreadsheet-shoes': 'https://readdy.ai/api/search-image?query=Colorful%20sneakers%20arranged%20in%20a%20row%20on%20a%20light%20concrete%20surface%2C%20vibrant%20footwear%20photography%2C%20bright%20studio%20lighting%2C%20energetic%20streetwear%20composition&width=1200&height=500&seq=art-hero-shoes&orientation=landscape',
  'cssbuy-spreadsheet-hoodies': 'https://readdy.ai/api/search-image?query=Stack%20of%20folded%20pastel%20hoodies%20on%20a%20soft%20lavender%20background%2C%20cozy%20fashion%20photography%2C%20gentle%20natural%20light%2C%20clean%20minimal%20composition&width=1200&height=500&seq=art-hero-hoodies&orientation=landscape',
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, '');

  const article = articleContents.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <p className="text-foreground-600 mb-6">Article not found.</p>
          <button onClick={() => navigate('/')} className="text-primary-500 hover:underline">
            {t('back_to_home')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedTitles = article.related
    .map((rSlug) => articleContents.find((a) => a.slug === rSlug))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('back_to_home')}
            </button>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
                {t(article.categoryKey)}
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{t('published_date')}: {article.date}</span>
                <span>{t('updated_date')}: 2026-08-27</span>
                <span>{article.readTime} read</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src={heroImages[article.slug]}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-8">
              {article.intro.map((p, i) => (
                <p key={`intro-${i}`}>{p}</p>
              ))}

              {article.sections.map((section, i) => (
                <section key={`section-${i}`}>
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, j) => (
                    <p key={`p-${i}-${j}`} className="mb-3">{p}</p>
                  ))}
                </section>
              ))}

              <section className="bg-background-100 rounded-xl p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
                  {t('faq')}
                </h2>
                <div className="space-y-4">
                  {article.faq.map((item, i) => (
                    <div key={`faq-${i}`}>
                      <h3 className="font-medium text-foreground-900 mb-1">{item.question}</h3>
                      <p className="text-sm">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {relatedTitles.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  {t('related_articles')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {relatedTitles.map((rel) => (
                    <button
                      key={rel!.slug}
                      onClick={() => navigate(`/${rel!.slug}`)}
                      className="flex items-center gap-2 p-4 bg-white rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                    >
                      <i className="ri-article-line text-primary-400" />
                      <span className="text-sm text-foreground-700">{rel!.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  {t('final_cta')}
                </h3>
                <p className="text-sm text-foreground-600 mb-6">
                  {t('final_cta_desc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-external-link-line" />
                    {t('explore_spreadsheet')}
                  </a>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-foreground-700 font-medium rounded-lg border border-background-200 hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-home-4-line" />
                    {t('home')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}